import { useEffect, useState } from 'react';
import { Button, List, Skeleton } from 'antd';
import { ArrowDownIcon, CloseXIcon } from 'icons';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import { useAppSelector } from 'store';

import { ISubjectData, parseSubjectName, useSubjectsData } from 'modules/global';

import { Modal } from 'components';

const SubjectsDrawer = () => {
  const t = useTranslations();
  const [selectedSubject, setSelectedSubject] = useQueryParam('subject', withDefault(StringParam, t('barchasi')));

  const { subjects, isLoading: subjectsLoading } = useSubjectsData();
  const [tempSubjectName, setTempSubjectName] = useState<string | undefined | null>(selectedSubject);

  const [open, setOpen] = useState(false);
  const isLoading = useAppSelector(state => state.routeLoading.isRouteLoading);

  const selectedSubj: ISubjectData | undefined =
    subjects?.subject?.find(subject => parseSubjectName(subject.name) === tempSubjectName) || subjects?.subject?.[0];

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onSave = () => {
    if (tempSubjectName === t('barchasi')) {
      setSelectedSubject(undefined);
    } else {
      setSelectedSubject(tempSubjectName);
    }
  };

  const onSelectSubject = (subject: string) => {
    setTempSubjectName(parseSubjectName(subject));
  };

  useEffect(() => {
    if (!isLoading) {
      handleClose();
    }
  }, [isLoading]);

  useEffect(() => {
    if (selectedSubject) {
      setTempSubjectName(selectedSubject);
    }
  }, [selectedSubject]);

  return (
    <>
      <div>
        <Button className="!flex !max-w-40 !items-center !rounded-xl !px-4 !py-5 sm:!max-w-full" onClick={handleOpen}>
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="relative h-5 w-5 shrink-0">
              <Image
                src={selectedSubj?.image || '/images/subject-barchasi.webp'}
                alt={selectedSubj?.name?.slice(0, 1) || 'Subject alt'}
                loading="lazy"
                fill
                title="Subject list"
              />
            </div>
            <p className="overflow-hidden truncate text-ellipsis whitespace-nowrap">
              {selectedSubj?.name || t('barchasi')}
            </p>
          </div>
          <ArrowDownIcon />
        </Button>

        <Modal
          open={open}
          height='70%'
          onClose={handleClose}
          footer={
            <Button
              loading={isLoading}
              onClick={onSave}
              className="!my-2 !w-full !rounded-lg !border-transparent !bg-green-500 !py-5 !text-white"
            >
              {t('profile.saqlash')}
            </Button>
          }
          closeIcon={
            <div className="flex items-center justify-center rounded-full bg-gray-800 p-1 text-white">
              <CloseXIcon size="12" />
            </div>
          }
          title={<p className="text-gray-400">{t('Mavzular')}</p>}
        >
          {subjectsLoading ? (
            <div className="mt-4 space-y-3">
              {Array.from({ length: 10 }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton.Button key={index} active className="!h-10 !w-full" />
              ))}
            </div>
          ) : (
            <div className="mt-2">
              <List
                loading={subjectsLoading}
                itemLayout="horizontal"
                dataSource={subjects?.subject}
                renderItem={item => {
                  const isActive = parseSubjectName(tempSubjectName || '') === parseSubjectName(item.name);

                  return (
                    <List.Item onClick={() => onSelectSubject(item.name)} className="!py-1">
                      <List.Item.Meta
                        className="cursor-pointer !items-center rounded-md !py-2 pl-4 transition-all"
                        style={{
                          background: isActive ? `${item.color}1f` : 'transparent',
                          borderRight: `3px solid ${isActive ? item.color : 'transparent'}`
                        }}
                        avatar={
                          <Image
                            src={item.image}
                            alt={item.name.slice(0, 1)}
                            loading="lazy"
                            width={32}
                            height={32}
                            title="Subject list"
                          />
                        }
                        title={
                          <span
                            style={{
                              color: isActive ? item.color : 'black',
                              fontWeight: isActive ? '500' : 'normal'
                            }}
                          >
                            {item.name}
                          </span>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default SubjectsDrawer;
